import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";
import { api } from "../lib/api";

const Field = ({ label, children }) => (
  <label className="block"><span className="text-xs font-bold text-gray-500">{label}</span>{children}</label>
);

export default function Settings() {
  const [s, setS] = useState(null);
  const [staff, setStaff] = useState([]);
  const [audit, setAudit] = useState([]);
  const [tgLink, setTgLink] = useState(null);
  const [newStaff, setNewStaff] = useState({ name: "", phone: "", role: "staff", location: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/admin/staff").then((r) => setStaff(r.data));
    api.get("/admin/audit?limit=30").then((r) => setAudit(r.data));
    api.get("/admin/staff-telegram-link").then((r) => setTgLink(r.data)).catch(() => {});
  }, []);
  if (!s) return <div className="p-10 text-center text-gray-400 font-bold">Загрузка...</div>;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", {
        brand: s.brand, telegram: { test_mode: s.telegram.test_mode, timeout: +s.telegram.timeout, attempts: +s.telegram.attempts },
        email: { sender_name: s.email.sender_name, subject: s.email.subject, code_ttl: +s.email.code_ttl, attempts: +s.email.attempts },
        staff_secret: s.staff_secret, loyalty: { min_interval_hours: +s.loyalty.min_interval_hours },
        marketing: { cooldown_days: +s.marketing.cooldown_days, strategy: s.marketing.strategy },
        birthday: { enabled: !!s.birthday?.enabled, channel: s.birthday?.channel || "both", subject: s.birthday?.subject || "", message: s.birthday?.message || "", promo_code: s.birthday?.promo_code || "" },
      });
      setS({ ...s, ...data });
      toast.success("Настройки сохранены");
    } catch (e) { toast.error("Ошибка"); }
    setBusy(false);
  };
  const addStaff = async () => {
    const { data } = await api.post("/admin/staff", newStaff);
    setStaff([...staff, data]);
    setNewStaff({ name: "", phone: "", role: "staff", location: "" });
  };

  return (
    <div className="space-y-4" data-testid="admin-settings">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-2xl">Настройки</h1>
        <button data-testid="settings-save-btn" className="admin-btn" disabled={busy} onClick={save}>Сохранить</button>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
          <div className="text-sm font-extrabold">Telegram API</div>
          <div className="text-xs font-semibold text-gray-500">Bot token: {s.telegram.token_set ? <span className="text-green-600">✓ настроен (backend .env)</span> : <span className="text-red-500">не задан</span>} · Bot: @{s.telegram.bot_username || "—"}</div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Timeout (сек)"><input data-testid="tg-timeout-input" type="number" className="admin-input" value={s.telegram.timeout} onChange={(e) => setS({ ...s, telegram: { ...s.telegram, timeout: e.target.value } })} /></Field>
            <Field label="Попытки"><input type="number" className="admin-input" value={s.telegram.attempts} onChange={(e) => setS({ ...s, telegram: { ...s.telegram, attempts: e.target.value } })} /></Field>
            <label className="flex items-end gap-1.5 text-xs font-bold pb-2.5">
              <input data-testid="tg-test-mode-toggle" type="checkbox" checked={!!s.telegram.test_mode} onChange={(e) => setS({ ...s, telegram: { ...s.telegram, test_mode: e.target.checked } })} /> Тестовый режим
            </label>
          </div>
          <div className="bg-[#FBF3E4] rounded-xl p-3 space-y-2" data-testid="tg-cert-confirm-block">
            <div className="text-xs font-extrabold text-gray-600">Подтверждение сертификатов через Telegram</div>
            <p className="text-xs text-gray-500 font-semibold">Кассир открывает бота и нажимает «Start» — после этого он сможет подтверждать списание подарочных сертификатов прямо в Telegram.</p>
            {tgLink?.link ? (
              <a data-testid="tg-cert-connect-btn" href={tgLink.link} target="_blank" rel="noreferrer" className="admin-btn text-xs !h-8"><Send size={13} /> Открыть бота @{tgLink.bot}</a>
            ) : (
              <div className="text-xs text-red-500 font-bold">Бот не настроен (TELEGRAM_BOT_TOKEN)</div>
            )}
            {tgLink?.cashiers?.length > 0 && (
              <div className="text-xs font-semibold text-gray-500">
                Подключены: {tgLink.cashiers.map((c) => c.name || (c.telegram_username ? `@${c.telegram_username}` : "кассир")).join(", ")}
              </div>
            )}
            {tgLink && !(tgLink.cashiers || []).length && <div className="text-xs text-gray-400 font-semibold">Пока никто не подключён</div>}
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
          <div className="text-sm font-extrabold">Resend API (Email)</div>
          <div className="text-xs font-semibold text-gray-500">API key: {s.email.api_key_set ? <span className="text-green-600">✓ настроен (backend .env)</span> : <span className="text-red-500">не задан</span>} · Sender: {s.email.sender_email}</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Имя отправителя"><input data-testid="email-sender-name-input" className="admin-input" value={s.email.sender_name} onChange={(e) => setS({ ...s, email: { ...s.email, sender_name: e.target.value } })} /></Field>
            <Field label="Тема письма"><input className="admin-input" value={s.email.subject} onChange={(e) => setS({ ...s, email: { ...s.email, subject: e.target.value } })} /></Field>
            <Field label="Срок кода (сек)"><input type="number" className="admin-input" value={s.email.code_ttl} onChange={(e) => setS({ ...s, email: { ...s.email, code_ttl: e.target.value } })} /></Field>
            <Field label="Лимит попыток"><input type="number" className="admin-input" value={s.email.attempts} onChange={(e) => setS({ ...s, email: { ...s.email, attempts: e.target.value } })} /></Field>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
          <div className="text-sm font-extrabold">Сотрудники и лояльность</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Секретный код сотрудника"><input data-testid="staff-secret-input" className="admin-input" value={s.staff_secret} onChange={(e) => setS({ ...s, staff_secret: e.target.value })} /></Field>
            <Field label="Мин. интервал посещений (ч)"><input type="number" className="admin-input" value={s.loyalty.min_interval_hours} onChange={(e) => setS({ ...s, loyalty: { ...s.loyalty, min_interval_hours: e.target.value } })} /></Field>
          </div>
          <div className="text-xs font-bold text-gray-500 pt-1">Сотрудники</div>
          {staff.map((st) => (
            <div key={st.id} className="flex items-center gap-2 text-sm">
              <span className="font-bold">{st.name}</span><span className="text-gray-400 text-xs">{st.role === "manager" ? "менеджер" : "кассир"} · {st.location || "—"}</span>
              <button onClick={async () => { await api.delete(`/admin/staff/${st.id}`); setStaff(staff.filter((x) => x.id !== st.id)); }} className="ml-auto text-red-400"><Trash2 size={13} /></button>
            </div>
          ))}
          <div className="flex gap-2">
            <input data-testid="staff-new-name" className="admin-input !h-8 text-xs" placeholder="Имя" value={newStaff.name} onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })} />
            <input className="admin-input !h-8 text-xs" placeholder="Локация" value={newStaff.location} onChange={(e) => setNewStaff({ ...newStaff, location: e.target.value })} />
            <select className="admin-input !w-28 !h-8 text-xs" value={newStaff.role} onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}>
                <option value="staff">Кассир</option><option value="manager">Менеджер</option>
            </select>
            <button data-testid="staff-add-btn" className="admin-btn !h-8 text-xs" disabled={!newStaff.name} onClick={addStaff}><Plus size={12} /></button>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3" data-testid="birthday-settings">
          <div className="flex items-center justify-between">
            <div className="text-sm font-extrabold">Автопоздравления с Днём рождения</div>
            <label className="text-xs font-bold flex items-center gap-1.5">
              <input data-testid="birthday-enabled-toggle" type="checkbox" checked={!!s.birthday?.enabled} onChange={(e) => setS({ ...s, birthday: { ...s.birthday, enabled: e.target.checked } })} /> Включено
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Канал">
              <select data-testid="birthday-channel-select" className="admin-input" value={s.birthday?.channel || "both"} onChange={(e) => setS({ ...s, birthday: { ...s.birthday, channel: e.target.value } })}>
                <option value="both">Telegram + Email</option><option value="telegram">Только Telegram</option><option value="email">Только Email</option>
              </select>
            </Field>
            <Field label="Промокод-подарок (пусто = просто поздравление)">
              <input data-testid="birthday-promo-input" className="admin-input" placeholder="BDAY-CRAFT" value={s.birthday?.promo_code || ""} onChange={(e) => setS({ ...s, birthday: { ...s.birthday, promo_code: e.target.value.toUpperCase() } })} />
            </Field>
          </div>
          <Field label="Тема письма"><input className="admin-input" value={s.birthday?.subject || ""} onChange={(e) => setS({ ...s, birthday: { ...s.birthday, subject: e.target.value } })} /></Field>
          <Field label="Текст поздравления ({first_name} — имя клиента)">
            <textarea data-testid="birthday-message-input" className="admin-input" value={s.birthday?.message || ""} onChange={(e) => setS({ ...s, birthday: { ...s.birthday, message: e.target.value } })} />
          </Field>
          <div className="flex items-center gap-2">
            <button data-testid="birthday-run-btn" className="admin-btn admin-btn-ghost text-xs" disabled={busy} onClick={async () => {
              try {
                const { data } = await api.post("/admin/birthday/run");
                if (data.enabled === false) toast.error("Автопоздравления выключены — включите и сохраните настройки");
                else if (data.matched === 0) toast.info("Сегодня именинников нет. Поздравление отправится автоматически в день рождения клиента");
                else toast.success(`Именинников сегодня: ${data.matched}, поздравлено: ${data.greeted}${data.skipped_no_consent ? `, без согласия на рассылку: ${data.skipped_no_consent}` : ""}`, { duration: 8000 });
              }
              catch (e) { toast.error("Ошибка запуска"); }
            }}>Запустить сейчас</button>
            <span className="text-xs text-gray-400 font-semibold">Проверка выполняется автоматически каждый час. Сначала сохраните настройки.</span>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-3">
          <div className="text-sm font-extrabold">Маркетинг</div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Пауза между рассылками (дней)"><input type="number" className="admin-input" value={s.marketing.cooldown_days} onChange={(e) => setS({ ...s, marketing: { ...s.marketing, cooldown_days: e.target.value } })} /></Field>
            <Field label="Очерёдность отправки по умолчанию">
              <select className="admin-input" value={s.marketing.strategy} onChange={(e) => setS({ ...s, marketing: { ...s.marketing, strategy: e.target.value } })}>
                <option value="least_recently_contacted">Сначала давно не получавшие (рек.)</option>
                <option value="newest_first">Сначала новые клиенты</option><option value="oldest_first">Сначала давние клиенты</option><option value="random">Случайный порядок</option>
              </select>
            </Field>
          </div>
          <div className="text-sm font-extrabold pt-2">Очистка демо-данных</div>
          <p className="text-xs text-gray-400 font-semibold">Удалит тестовых клиентов (demo…@craftmail.uz), их заявки и визиты, обнулит статистику сканов/графиков. Кампании, баннеры, реальные клиенты, сертификаты и отзывы сайта останутся.</p>
          <button data-testid="cleanup-demo-btn" className="admin-btn admin-btn-ghost text-xs !text-red-500" disabled={busy} onClick={async () => {
            if (!window.confirm("Удалить демо-данные и обнулить статистику? Действие необратимо.")) return;
            setBusy(true);
            try {
              const { data } = await api.post("/admin/cleanup-demo-data");
              const r = data.removed || {};
              toast.success(`Удалено: клиентов ${r.customers}, заявок ${r.leads}, событий ${r.events}`);
            } catch (e) { toast.error("Ошибка очистки"); }
            setBusy(false);
          }}><Trash2 size={12} /> Удалить демо-данные</button>
          <div className="text-sm font-extrabold pt-2">Журнал действий</div>
          <div className="max-h-48 overflow-y-auto space-y-1">
            {audit.map((a) => (
              <div key={a.id} className="flex justify-between text-xs border-b border-gray-50 py-1">
                <span><b>{a.actor}</b> · {a.action}</span><span className="text-gray-400">{a.ts?.slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
